import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import StepLayout from './components/StepLayout';
import LiveCode from './components/LiveCode';
import EmailCode from './components/EmailCode';

// Slice C — the seller-facing ID-badge verification page. A magic link
// `/badge/<token>` opens this instead of the takeover wizard (index.js
// branches on the path). Backend: Slice B1 endpoints (badge/start,
// badge/credentials, badge/live-code) + the existing id-verification/*.
// Every hardened account uses authenticator 2FA (design §15), so the code
// step is always the live-updating TOTP; the email-code fallback + mailbox
// prepare are B2 and deliberately not here.

function tokenFromUrl() {
  if (typeof window === 'undefined') return '';
  const m = window.location.pathname.match(/\/badge\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

export default function BadgeVerifyApp() {
  const [phase, setPhase] = useState('loading');
  const [sessionId, setSessionId] = useState(null);
  const [creds, setCreds] = useState(null);
  const [verifyLink, setVerifyLink] = useState(null);
  const [error, setError] = useState('');
  const [copiedField, setCopiedField] = useState('');
  const pollRef = useRef(null);

  // Step 0 — exchange the magic-link token for a bound session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = tokenFromUrl();
      if (!token) { setError('This link looks incomplete.'); setPhase('error'); return; }
      try {
        const r = await api.startBadge(token);
        if (cancelled) return;
        setSessionId(r.sessionId);
        setPhase('intro');
      } catch (e) {
        if (cancelled) return;
        setError(e && e.status === 401
          ? "This link has expired. Message us and we'll send a fresh one."
          : 'Something went wrong opening this link.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copy = useCallback((field, value) => {
    if (!value) return;
    try {
      navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 1500);
    } catch { /* clipboard blocked — value is still visible to type */ }
  }, []);

  const goCreds = useCallback(async () => {
    setPhase('creds');
    try {
      const c = await api.getBadgeCredentials(sessionId);
      setCreds(c);
    } catch {
      setCreds({ email: '', password: '', _error: true });
    }
  }, [sessionId]);

  const goVerify = useCallback(() => {
    setPhase('verify');
    setVerifyLink(null);
    api.initiateIdVerification(sessionId).catch(() => {});
  }, [sessionId]);

  // Poll for the verification link once initiated.
  useEffect(() => {
    if (phase !== 'verify' || verifyLink) return undefined;
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const s = await api.getStatus(sessionId);
        if (s.idVerificationState === 'verified' || s.idVerificationState === 'in_review') { setPhase('success'); return; }
        if (s.idVerificationLink) { setVerifyLink(s.idVerificationLink); return; }
      } catch { /* keep polling */ }
      if (n < 40) pollRef.current = setTimeout(tick, 3000);
    };
    pollRef.current = setTimeout(tick, 1500);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [phase, verifyLink, sessionId]);

  const goChecking = useCallback(() => {
    setPhase('checking');
    api.checkIdVerification(sessionId).catch(() => {});
  }, [sessionId]);

  // Poll for the badge landing (in_review/verified) after the seller finishes Persona.
  useEffect(() => {
    if (phase !== 'checking') return undefined;
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const s = await api.getStatus(sessionId);
        if (s.idVerificationState === 'verified' || s.idVerificationState === 'in_review') { setPhase('success'); return; }
      } catch { /* keep polling */ }
      if (n < 20) pollRef.current = setTimeout(tick, 3000);
      else setPhase('checktimeout');
    };
    pollRef.current = setTimeout(tick, 2000);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [phase, sessionId]);

  const askForHelp = useCallback(() => {
    // Fire-and-forget Slack escalation (B2); the seller always lands on 'help'.
    if (sessionId) { api.badgeHelp(sessionId).catch(() => {}); }
    setPhase('help');
  }, [sessionId]);

  function HelpLink() {
    return (
      <button type="button" className="badge-linkbtn badge-help" onClick={askForHelp}>
        {"Something's wrong?"}
      </button>
    );
  }

  if (phase === 'loading') {
    return <StepLayout title="One moment…" variant="centered" hideTrustpilot><p className="badge-p">Opening your verification…</p></StepLayout>;
  }

  if (phase === 'error') {
    return <StepLayout title="We hit a snag" variant="centered" hideTrustpilot><p className="badge-p">{error}</p></StepLayout>;
  }

  if (phase === 'help') {
    return (
      <StepLayout title="We're on it" variant="centered" hideTrustpilot>
        <p className="badge-p">{"Thanks. We've been notified and someone will message you shortly. You can close this page."}</p>
      </StepLayout>
    );
  }

  if (phase === 'intro') {
    return (
      <StepLayout title="Verify your LinkedIn, earn £5" variant="centered" hideTrustpilot>
        <p className="badge-p"><strong>You don&apos;t need your old login. We give you a fresh one to use.</strong></p>
        <p className="badge-p">{"It only takes about 2 minutes: sign in with the details we provide, confirm your identity with a photo of your ID and a selfie, and we send you £5."}</p>
        <button type="button" className="primary-cta badge-cta" onClick={() => setPhase('app')}>{"Let's go"}</button>
        <HelpLink />
      </StepLayout>
    );
  }

  if (phase === 'app') {
    return (
      <StepLayout title="First, get the LinkedIn app" variant="centered" hideTrustpilot>
        <p className="badge-p">{"Open the LinkedIn app on your phone (download it from the App Store or Google Play if you don't have it). The ID check only works in the app."}</p>
        <button type="button" className="primary-cta badge-cta" onClick={goCreds}>I have the app</button>
        <HelpLink />
      </StepLayout>
    );
  }

  if (phase === 'creds') {
    return (
      <StepLayout title="Your login details" variant="centered" hideTrustpilot>
        <p className="badge-p">{"Sign into the LinkedIn app with exactly these, not your own email:"}</p>
        <div className="badge-cred">
          <div className="badge-cred-label">Email</div>
          <button type="button" className="badge-cred-val" onClick={() => copy('email', creds && creds.email)}>{(creds && creds.email) || '…'}</button>
          <div className="badge-cred-copy">{copiedField === 'email' ? 'Copied!' : 'tap to copy'}</div>
        </div>
        <div className="badge-cred">
          <div className="badge-cred-label">Password</div>
          <button type="button" className="badge-cred-val" onClick={() => copy('password', creds && creds.password)}>{(creds && creds.password) || '…'}</button>
          <div className="badge-cred-copy">{copiedField === 'password' ? 'Copied!' : 'tap to copy'}</div>
        </div>
        <p className="badge-note">{"It will ask for a verification code. We'll show it on the next screen."}</p>
        <button type="button" className="primary-cta badge-cta" onClick={() => setPhase('code')} disabled={!creds}>I need a code</button>
        <HelpLink />
      </StepLayout>
    );
  }

  if (phase === 'code') {
    return (
      <StepLayout title="Your verification code" variant="centered" hideTrustpilot>
        <p className="badge-p">Type this code into LinkedIn. It refreshes automatically. Always use the one showing now.</p>
        <LiveCode fetchCode={() => api.getBadgeLiveCode(sessionId)} />
        <EmailCode
          start={() => api.startBadgeEmailCode(sessionId)}
          poll={() => api.getBadgeEmailCode(sessionId)}
        />
        <button type="button" className="primary-cta badge-cta" onClick={goVerify}>I have logged in</button>
        <HelpLink />
      </StepLayout>
    );
  }

  if (phase === 'verify') {
    return (
      <StepLayout title="Verify your identity" variant="centered" hideTrustpilot>
        {verifyLink ? (
          <>
            <p className="badge-p">{"Tap the button below, then follow LinkedIn's steps, a photo of your passport or driving licence, and a selfie."}</p>
            <a className="primary-cta badge-cta badge-link" href={verifyLink} target="_blank" rel="noreferrer">Tap here to verify</a>
            <button type="button" className="primary-cta badge-cta badge-cta--secondary" onClick={goChecking}>I have done the verification</button>
          </>
        ) : (
          <p className="badge-p">Setting up your verification link… this takes a few seconds.</p>
        )}
        <HelpLink />
      </StepLayout>
    );
  }

  if (phase === 'checking') {
    return (
      <StepLayout title="Checking…" variant="centered" hideTrustpilot>
        <p className="badge-p">Confirming your verification with LinkedIn, one moment.</p>
        <HelpLink />
      </StepLayout>
    );
  }

  if (phase === 'checktimeout') {
    return (
      <StepLayout title="Almost there" variant="centered" hideTrustpilot>
        <p className="badge-p">{"We haven't seen it land yet. If you finished the ID check, tap below to check again, otherwise re-open the verify link."}</p>
        <button type="button" className="primary-cta badge-cta" onClick={goChecking}>Check again</button>
        <button type="button" className="primary-cta badge-cta badge-cta--secondary" onClick={() => setPhase('verify')}>Back to the link</button>
        <HelpLink />
      </StepLayout>
    );
  }

  if (phase === 'success') {
    return (
      <StepLayout title="You're verified! 🎉" variant="centered" hideTrustpilot>
        <p className="badge-p">{"Thanks. That's everything. We'll send your £5 shortly. You can log out and close the app now."}</p>
      </StepLayout>
    );
  }

  return null;
}
